import React, { ReactNode, useContext, useRef } from "react";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { BasicBotData, BotType } from "@shared/types";
import BotPreviewCard from "features/bots/MyBots/BotPreviewCard";
import FlatList from "common/Lists/FlatList";
import BotsStatistics from "features/bots/MyBots/BotsStatistics";
import { BotsBulkActionsContext } from "@shared/features/bots/BotsBulkActionsProvider";
import { CopyToClipboardButton } from "common/CopyToClipboard/CopyToClipboard";
import { getTvWebhookBaseUrl } from "app/shared/utils";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import Flex from "common/Boxes/Flex";
import { useTheme } from "styled-components";
import NewLookBotCard from "features/bots/NewLook/NewLookBotCard";
import NewLookBotsPerformance from "features/bots/NewLook/NewLookBotsPerformance";
import NewLookBotCardSkeleton from "features/bots/NewLook/NewLookBotCardSkeleton";
import { Copy } from "lucide-react";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout";
import {
  getNewLookSparkline,
  NewLookBotSparklines,
} from "features/bots/NewLook/useNewLookBotSparklines";

interface MobileBotsCardsProps {
  emptyState: ReactNode;
  /** New look: the cards' sparklines (see BotsCards). */
  sparklines?: NewLookBotSparklines;
}

const MobileBotsCards = ({ emptyState, sparklines }: MobileBotsCardsProps) => {
  const { botType, bots, isChannelConfiguration, getBotsDataStatus } =
    useContext(MyBotsPageContext)!;
  const { isBulkMode } = useContext(BotsBulkActionsContext)!;
  const botsStatisticsRef = useRef<{ refetch?: () => void }>(undefined);
  const { isNewLook } = useTheme();
  const isLoading =
    !getBotsDataStatus.currentData || getBotsDataStatus.isLoading;
  // New look: without any bots the empty state stands alone (no zeroed
  // figures above it).
  const hideNewLookStatistics =
    isNewLook && !isLoading && !getBotsDataStatus.data?.data?.length;
  // New look: the first load shows card-shaped placeholders, not a spinner.
  const showNewLookSkeletons =
    isNewLook && getBotsDataStatus.isLoading && !bots?.length;

  const webhookUrlButton =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS ? (
      <Flex
        style={
          isNewLook
            ? NEW_LOOK_WEBHOOK_ROW_STYLE
            : { justifyContent: "flex-end", paddingBottom: 12 }
        }
      >
        <CopyToClipboardButton
          name="Webhook URL"
          text={getTvWebhookBaseUrl()}
          icon={
            isNewLook ? (
              <Copy size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
            ) : (
              <CopyOutlined />
            )
          }
          type={isNewLook ? "default" : "primary"}
          shape={undefined}
          style={isNewLook ? { width: "100%", height: 40 } : undefined}
        >
          Webhook URL
        </CopyToClipboardButton>
      </Flex>
    ) : undefined;

  const renderContent = ({
    item,
    index,
  }: {
    item: BasicBotData | SkeletonItem;
    index: number;
  }) => {
    if (isSkeletonItem(item)) return <NewLookBotCardSkeleton key={item.key} />;
    if (isNewLook) {
      return (
        <NewLookBotCard
          key={item?.id}
          botData={item}
          id={`my-bot-${index + 1}`}
          sparkline={getNewLookSparkline(sparklines, item.id)}
        />
      );
    }
    return (
      <BotPreviewCard
        key={item?.id}
        botData={item}
        id={`my-bot-${index + 1}`}
      />
    );
  };

  const renderTopContainer = () => (
    <>
      {webhookUrlButton}
      {!isBulkMode && !isChannelConfiguration && !hideNewLookStatistics && (
        <div style={{ marginBottom: isNewLook ? 12 : 8 }}>
          {isNewLook ? (
            <NewLookBotsPerformance
              botType={botType}
              ref={botsStatisticsRef}
              isLoading={isLoading}
              compact={true}
            />
          ) : (
            <BotsStatistics
              botType={botType}
              ref={botsStatisticsRef}
              isLoading={isLoading}
            />
          )}
        </div>
      )}
    </>
  );

  const onRefresh = () => {
    try {
      getBotsDataStatus.refetch();
      botsStatisticsRef.current?.refetch?.();
    } catch {}
  };

  return (
    <FlatList<BasicBotData | SkeletonItem>
      data={showNewLookSkeletons ? NEW_LOOK_SKELETONS : bots}
      loading={showNewLookSkeletons ? false : getBotsDataStatus.isLoading}
      renderContent={renderContent}
      renderTopContainer={renderTopContainer}
      onRefresh={onRefresh}
      refreshing={showNewLookSkeletons ? false : getBotsDataStatus.isFetching}
      emptyState={emptyState}
      hasActionButton={true}
      wrapperStyle={{ padding: 16 }}
    />
  );
};

export default MobileBotsCards;

const NEW_LOOK_WEBHOOK_ROW_STYLE = { paddingBottom: 12 };

interface SkeletonItem {
  key: string;
  isSkeleton: true;
}
const NEW_LOOK_SKELETONS: SkeletonItem[] = [0, 1].map((index) => ({
  key: `skeleton-${index}`,
  isSkeleton: true,
}));
const isSkeletonItem = (
  item: BasicBotData | SkeletonItem
): item is SkeletonItem => !!item && "isSkeleton" in item;
